import { useCallback } from 'react';
import { getUserState } from '@carvana/core-auth';
import { nanoid } from 'nanoid';

import { getBrowserCookieId, getBrowserData } from '../utilities/browserDataExtractor';
import AnalyticsApi from '../api/AnalyticsApi';

export type BrowserData = {
  referrerUrl: string;
  url: string;
  browserName: string;
  osName: string;
  deviceCategory?: string;
  deviceName?: string;
  language: string;
  screenResolution: string;
  browserResolution: string;
};

export type EventSource = 'system' | 'user_interaction';

export type EventAction =
  | 'Abandoned'
  | 'Changed'
  | 'Clicked'
  | 'Completed'
  | 'ExitedEarly'
  | 'Failed'
  | 'Launched'
  | 'Redirected'
  | 'Rejected'
  | 'Rendered'
  | 'Shown'
  | 'Uploaded'
  | 'Visited';

export type EventSourceContext = {
  component?: string;
  eventSource?: EventSource;
  eventAction?: EventAction;
  eventSourceId?: string;
  eventTime: Date;
  element?: string;
};

export type CustomAttributes = Record<string, unknown>;

export type VisitorContext = {
  browserCookieId: string;
  userId: string;
  userAgent: string;
  clientIp?: string;
};

export type ClickStreamEvent = {
  visitorContext: VisitorContext;
  browserContext: BrowserData;
  eventSourceContext: EventSourceContext;
  customAttributes: CustomAttributes;
};
const sessionId = nanoid();

const makeEvent = (
  eventSourceContext,
  customAttributes: CustomAttributes = {}
): ClickStreamEvent => {
  const action = eventSourceContext.eventAction ? ` - ${eventSourceContext.eventAction}` : '';
  const element = eventSourceContext.element ? `:${eventSourceContext.element}` : '';
  const eventName = `SellToCarvana - ${eventSourceContext.component}${element}${action}`;

  return {
    visitorContext: {
      browserCookieId: getBrowserCookieId(),
      userId: getUserState()?.user?.user_id || null,
      userAgent: window.navigator.userAgent
    },
    browserContext: getBrowserData(),
    eventSourceContext: {
      ...eventSourceContext,
      eventName,
      eventTime: new Date()
    },
    customAttributes: { ...customAttributes }
  };
};

const useAnalyticEvent = () => {
  const recordEvent = useCallback(
    (
      actionType: EventAction,
      component: string,
      element: string,
      customAttributes: CustomAttributes = {}
    ) => {
      AnalyticsApi.queue(
        makeEvent(
          {
            eventAction: actionType,
            element,
            eventSource: 'user_interaction',
            component
          },
          {
            ...customAttributes,
            sessionId
          }
        )
      );
    },
    []
  );

  return { recordEvent };
};

export default useAnalyticEvent;
